import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { purchaseOrdersApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@verlo/shared/components/ui/dialog';
import { qty } from '../../lib/orders';

// Mark confirmed (Oct 2026, mock "Sending orders" §5). The supplier replied to the email; what
// they said goes on the order: what they'll send of each line (it starts as ordered: change
// only what's different), when it's coming, and anything else they said. What's short goes
// back on To order, and Receive checks against what they said.

export default function ConfirmOrderDialog({ order, onClose, onDone }) {
  const [send, setSend] = useState({});
  const [when, setWhen] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!order) return;
    setSend(Object.fromEntries(order.lines.map((l) => [l.stock_id, String(l.confirmed ?? l.quantity)])));
    setWhen(order.deliver_on || '');
    setNote(order.confirm_note || '');
  }, [order]);

  if (!order) return null;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await purchaseOrdersApi.confirm(order.id, {
        lines: order.lines.map((l) => ({ stock_id: l.stock_id, quantity: Math.max(0, Number(send[l.stock_id]) || 0) })),
        deliver_on: when || null, note,
      });
      const short = data.lines.filter((l) => l.confirmed < l.quantity).length;
      toast.success(`${order.number} confirmed${short ? `; ${short} short, back on To order` : ''}`);
      onDone(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save it"); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="confirm-order-dialog">
        <DialogHeader>
          <DialogTitle>{order.supplier_name} confirmed {order.number}</DialogTitle>
          <DialogDescription>They&apos;ll send starts as ordered: change only what&apos;s different. What&apos;s short goes back on To order.</DialogDescription>
        </DialogHeader>
        <label className="flex items-center gap-3 text-[13px] text-ink-soft">Coming on
          <Input type="date" className="h-9 w-44" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="confirm-deliver-on" />
        </label>
        <div className="max-h-[50vh] overflow-y-auto rounded-md border border-hairline">
          <table className="w-full text-[13.5px]">
            <thead><tr className="border-b border-hairline bg-surface-2 text-left font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">
              <th className="px-3 py-1.5">Item</th><th className="px-3 py-1.5">Ordered</th><th className="px-3 py-1.5">They&apos;ll send</th></tr></thead>
            <tbody>{order.lines.map((l) => {
              const v = send[l.stock_id] ?? '';
              const short = Math.round((l.quantity - (Number(v) || 0)) * 1000) / 1000;
              return (
                <tr key={l.stock_id} className="border-b border-hairline last:border-0">
                  <td className="px-3 py-1.5 text-ink">{l.name}</td>
                  <td className="px-3 py-1.5 font-mono tabular-nums text-ink-soft">{qty(l.quantity, l.unit)}</td>
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-2">
                      <Input type="number" min="0" step="any" className="h-8 w-20 font-mono tabular-nums" value={v}
                        onChange={(e) => setSend((s) => ({ ...s, [l.stock_id]: e.target.value }))} aria-label={`${l.name}: they'll send`} data-testid={`confirm-line-${l.stock_id}`} />
                      {short > 0 && <Signal tone="warn" dot={false}>{qty(short, l.unit)} short</Signal>}
                    </div>
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
        <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="What they said, if anything: “hake for cod”" aria-label="What they said" data-testid="confirm-note" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-verlo hover:bg-verlo-hover" onClick={save} disabled={busy} data-testid="confirm-save">Confirmed</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
